"use strict";
/* eslint-disable no-restricted-syntax */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.findConflicts = void 0;
const path_1 = require("./path");
const VagueNode_1 = require("./VagueNode");
const isDeepEqual_1 = __importDefault(require("./isDeepEqual"));
const find = (iterable, predicate) => {
    for (const iterator of iterable) {
        if (predicate(iterator))
            return iterator;
    }
    return null;
};
const pathMatches = (path, pattern) => path === pattern || path.startsWith(`${pattern}/`);
/**
 * 处理两组 patches 合并修改。
 *
 * 默认保留 patches1 的修改，也可以通过第三个参数（`decisions`），在某些节点上，选择保留 patches2 的修改。
 *
 * 返回的 `conflicts` 是冲突点列表，特别注意：如果 findConflicts 传入了第三个参数（`decisions`），那么这个冲突点列表会随着第三参数变化：
 *
 * 以下是一个例子，说明了 `conflicts` 受 `decisions` 的影响
 *
 * 1. 第一次调用@paramsions` 为空，可能返回 `/aaa` 和 `/bbb`
 *
 * 2. 第二次调用，`decisions` 传入 `["/aaa"]`，返回列表里将不再有 `/aaa`，但是 `/bbb` 仍然存在。
 *
 *    - 在极少数情况下，之前被抑制(supressed)的冲突，此时可能会蹦出来，导致返回的列表为两条： `/aaa/foo/bar` 和 `/bbb`
 *
 *    - 不保障顺序，但是 conflicts 里每一条 path 都会是唯一的
 *
 * @param patches1
 * @param patches2
 * @param decisions 默认会保留 patches1 里的修改，如果在某些节点上要改变
 * @returns
 */
function findConflicts(patches1, patches2, decisions) {
    const root1 = new VagueNode_1.VagueNode('');
    const root2 = new VagueNode_1.VagueNode('');
    // fill two trees and merge patches
    for (const [root, patches, modifiedBy] of [
        [root1, patches1, 'user1'],
        [root2, patches2, 'user2'],
    ]) {
        for (let i = 0; i < patches.length; i++) {
            const patch = patches[i];
            const node = root.queryPath(patch.path);
            node.applyPatch(modifiedBy, patch);
        }
    }
    // pairwise comparision
    let outRoot = new VagueNode_1.VagueNode('');
    const pathQueue = [];
    const conflicts = [];
    // eslint-disable-next-line max-len
    function isStatusDeleteOrSet(node) {
        return node.status === VagueNode_1.VagueNodeStatus.DELETED || node.status === VagueNode_1.VagueNodeStatus.ASSIGNED;
    }
    function addConflictAndGetPatches(cp) {
        const { path } = cp;
        conflicts.push(cp);
        const decision = decisions && decisions.find(x => x.path === path);
        const patches = decision ? decision.patches : cp.patches1;
        return patches;
    }
    function applyPatchesToTarget(patches, pathPrefix, target) {
        for (const subPatch of patches) {
            if (!pathMatches(subPatch.path, pathPrefix))
                throw new Error('Cannot apply decision. Mismatched path');
            const subTarget = target.queryPath(subPatch.path.substr(pathPrefix.length));
            subTarget.applyPatch('merger', subPatch);
        }
    }
    function makeIndexMap(side) {
        if (!side.isResortedArray)
            return null;
        const ans = new Array(side.isResortedArray.length);
        side.children.forEach((x) => {
            ans[+x.id] = +x.originalId;
        });
        return ans;
    }
    function makeConflictPatches(path, node, indexMap, anotherIndexMap) {
        const patches = [];
        if (indexMap)
            patches.push({ op: 'resortArray', path, indexMap });
        node.children.forEach((childNode) => {
            const oldIdx = +childNode.originalId;
            if (!(0, VagueNode_1.isValidIndex)(oldIdx) || (anotherIndexMap && !anotherIndexMap.includes(oldIdx))) {
                const childPath = (0, path_1.pushPath)(path, childNode.id);
                for (const patch of childNode.toPatches(childPath))
                    patches.push(patch);
            }
        });
        return patches;
    }
    pathQueue.push({ path: '', left: root1, right: root2, target: outRoot });
    while (pathQueue.length > 0) {
        const { path, left, right, target } = pathQueue.shift();
        // if user made some extra patches via "decisions" to current target
        // just prune all sub-nodes.
        // TODO: this will shortcircuit all target's children, if `target` is a resortedArray
        if (target.lastModifiedBy === 'merger')
            continue;
        // pruned some meanless sub-trees
        if (!left && !right)
            continue;
        if (!(left === null || left === void 0 ? void 0 : left.isDirty) && !(right === null || right === void 0 ? void 0 : right.isDirty))
            continue;
        // one or two trees are dirty.
        // first of all, queue their children.
        const leftTouched = !!left && left.status !== VagueNode_1.VagueNodeStatus.NOT_TOUCHED;
        const rightTouched = !!right && right.status !== VagueNode_1.VagueNodeStatus.NOT_TOUCHED;
        // if current nodes are not touched,
        if (!leftTouched && !rightTouched) {
            // they are dirty only because of descendants
            // so just ignore current node, check the children
            const newOrigialIds = new Set();
            for (const child of [...((left === null || left === void 0 ? void 0 : left.children) || []), ...((right === null || right === void 0 ? void 0 : right.children) || [])]) {
                const oldId = child.originalId;
                if (newOrigialIds.has(oldId))
                    continue;
                newOrigialIds.add(oldId);
                pathQueue.push({
                    path: (0, path_1.pushPath)(path, oldId),
                    left: left && left.queryPath([oldId], { checkOriginalIdUntilMeetNodeModifiedBy: 'x' }),
                    right: right && right.queryPath([oldId], { checkOriginalIdUntilMeetNodeModifiedBy: 'x' }),
                    target: target.queryPath([oldId]),
                });
            }
            continue;
        }
        // if only one side modified, and the other tree is not dirty.
        // and it's safe to directly apply changes
        if (leftTouched !== rightTouched && !!(left === null || left === void 0 ? void 0 : left.isDirty) !== !!(right === null || right === void 0 ? void 0 : right.isDirty)) {
            // only one-side modified
            const modified = leftTouched ? left : right;
            if (target === outRoot) {
                outRoot = modified;
            }
            else {
                const parent = target.parent;
                target.remove();
                parent.append(modified);
                parent.markDirty();
            }
            // pre-prune
            continue;
        }
        // this shall never happen
        if (!left || !right)
            continue;
        // now both node are dirty
        // at least one side is modified
        if ([left, right].some(isStatusDeleteOrSet)) {
            const patches1 = Array.from(left.toPatches(path));
            const patches2 = Array.from(right.toPatches(path));
            const finalPatches = (0, isDeepEqual_1.default)(patches1, patches2)
                ? patches1
                : addConflictAndGetPatches({ path, originalPath: target.originalPath, patches1, patches2 });
            applyPatchesToTarget(finalPatches, path, target);
            continue;
        }
        // now due to arrays, things get complex
        {
            const indexMap1 = makeIndexMap(left);
            const indexMap2 = makeIndexMap(right);
            const patches1 = makeConflictPatches(path, left, indexMap1, indexMap2);
            const patches2 = makeConflictPatches(path, right, indexMap2, indexMap1);
            let finalPatches;
            if (patches1.length && patches2.length) {
                finalPatches = addConflictAndGetPatches({ path, originalPath: target.originalPath, patches1, patches2 });
            }
            else {
                finalPatches = patches1.length ? patches1 : patches2;
            }
            applyPatchesToTarget(finalPatches, path, target);
            const adopted = new Map(Array.from(target.children, x => [x.lastModifiedBy === 'merger' ? -1 : +x.originalId, x]));
            for (const child of [...left.children, ...right.children]) {
                const oldIndex = +child.originalId;
                const target = (0, VagueNode_1.isValidIndex)(oldIndex) && adopted.get(oldIndex);
                if (!target)
                    continue;
                adopted.delete(oldIndex);
                pathQueue.push({
                    path: (0, path_1.pushPath)(path, target.id),
                    left: find(left.children, node => +node.originalId === oldIndex),
                    right: find(right.children, node => +node.originalId === oldIndex),
                    target,
                });
            }
        }
    }
    return {
        mergedPatches: Array.from(outRoot.toPatches('')),
        conflicts,
    };
}
exports.findConflicts = findConflicts;
exports.default = findConflicts;
